import React from "react";
import { Box } from "@chakra-ui/react";

export const WarehouseBackground: React.FC = () => {
  return (
    <Box
      position="absolute"
      top="0"
      left="0"
      width="100%"
      height="100%"
      overflow="hidden"
      zIndex="0"
      pointerEvents="none"
    >
      {/* Dark warehouse interior gradient */}
      <Box
        position="absolute"
        width="100%"
        height="100%"
        background="linear-gradient(180deg, #1a1a1a 0%, #252525 30%, #2a2a2a 60%, #1f1f1f 100%)"
      />

      {/* Warehouse SVG */}
      <svg
        className="warehouse-svg"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      >
        <defs>
          {/* Crate gradients - various wood tones */}
          <linearGradient id="crateLight" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#c4a574" stopOpacity="1" />
            <stop offset="100%" stopColor="#a08050" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="crateMedium" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#a08050" stopOpacity="1" />
            <stop offset="100%" stopColor="#806030" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="crateDark" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#705020" stopOpacity="1" />
            <stop offset="100%" stopColor="#503010" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="crateRed" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#8b3030" stopOpacity="1" />
            <stop offset="100%" stopColor="#6b2020" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="crateGreen" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3a5a3a" stopOpacity="1" />
            <stop offset="100%" stopColor="#2a4a2a" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="crateBlue" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3a4a6a" stopOpacity="1" />
            <stop offset="100%" stopColor="#2a3a5a" stopOpacity="1" />
          </linearGradient>

          {/* Metal beam gradient */}
          <linearGradient id="metalBeam" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#404040" stopOpacity="1" />
            <stop offset="50%" stopColor="#606060" stopOpacity="1" />
            <stop offset="100%" stopColor="#404040" stopOpacity="1" />
          </linearGradient>

          {/* Light cone gradient */}
          <linearGradient id="lightCone" x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor="#ffffe0" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#ffffe0" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Warehouse ceiling beams */}
        <g className="ceiling-beams">
          <rect x="0" y="0" width="1440" height="15" fill="url(#metalBeam)" />
          <rect x="100" y="0" width="20" height="100" fill="url(#metalBeam)" />
          <rect x="400" y="0" width="20" height="120" fill="url(#metalBeam)" />
          <rect x="720" y="0" width="25" height="110" fill="url(#metalBeam)" />
          <rect x="1020" y="0" width="20" height="130" fill="url(#metalBeam)" />
          <rect x="1320" y="0" width="20" height="105" fill="url(#metalBeam)" />
        </g>

        {/* Overhead lights */}
        <g className="warehouse-lights">
          <ellipse cx="250" cy="80" rx="30" ry="10" fill="#ffffe0" className="warehouse-light-1" />
          <polygon points="220,90 280,90 320,400 180,400" fill="url(#lightCone)" className="light-cone-1" />

          <ellipse cx="720" cy="70" rx="35" ry="12" fill="#ffffe0" className="warehouse-light-2" />
          <polygon points="685,82 755,82 810,450 630,450" fill="url(#lightCone)" className="light-cone-2" />

          <ellipse cx="1180" cy="85" rx="30" ry="10" fill="#ffffe0" className="warehouse-light-3" />
          <polygon points="1150,95 1210,95 1260,420 1100,420" fill="url(#lightCone)" className="light-cone-3" />
        </g>

        {/* Back wall shelving/crates - far back, darker */}
        <g className="crates-back" opacity="0.6">
          <rect x="50" y="200" width="80" height="60" fill="url(#crateDark)" />
          <rect x="140" y="180" width="70" height="80" fill="url(#crateDark)" />
          <rect x="220" y="210" width="60" height="50" fill="url(#crateDark)" />
          <rect x="350" y="190" width="90" height="70" fill="url(#crateDark)" />
          <rect x="500" y="200" width="75" height="60" fill="url(#crateDark)" />
          <rect x="600" y="175" width="85" height="85" fill="url(#crateDark)" />
          <rect x="750" y="195" width="70" height="65" fill="url(#crateDark)" />
          <rect x="880" y="185" width="80" height="75" fill="url(#crateDark)" />
          <rect x="1000" y="200" width="65" height="60" fill="url(#crateDark)" />
          <rect x="1100" y="180" width="90" height="80" fill="url(#crateDark)" />
          <rect x="1220" y="195" width="75" height="65" fill="url(#crateDark)" />
          <rect x="1320" y="210" width="80" height="50" fill="url(#crateDark)" />
        </g>

        {/* Left stack - precariously leaning */}
        <g className="crate-stack-left">
          {/* Base crates */}
          <rect x="20" y="750" width="120" height="100" fill="url(#crateMedium)" />
          <rect x="150" y="760" width="100" height="90" fill="url(#crateLight)" />
          <rect x="30" y="650" width="110" height="95" fill="url(#crateLight)" />
          <rect x="145" y="670" width="90" height="85" fill="url(#crateMedium)" />
          {/* Middle layer */}
          <rect x="40" y="555" width="100" height="90" fill="url(#crateRed)" transform="rotate(-2 90 600)" />
          <rect x="150" y="580" width="80" height="85" fill="url(#crateMedium)" />
          {/* Upper layer - more tilted */}
          <rect x="55" y="465" width="90" height="85" fill="url(#crateGreen)" transform="rotate(3 100 507)" />
          <rect x="150" y="490" width="70" height="80" fill="url(#crateLight)" transform="rotate(-4 185 530)" />
          {/* Top - very precarious */}
          <rect x="70" y="380" width="80" height="75" fill="url(#crateBlue)" transform="rotate(5 110 417)" />
          <rect x="90" y="305" width="60" height="65" fill="url(#crateMedium)" transform="rotate(-6 120 337)" />
        </g>

        {/* Center-left stack */}
        <g className="crate-stack-center-left">
          <rect x="320" y="720" width="130" height="130" fill="url(#crateLight)" />
          <rect x="460" y="750" width="100" height="100" fill="url(#crateMedium)" />
          <rect x="330" y="600" width="110" height="115" fill="url(#crateMedium)" />
          <rect x="450" y="640" width="95" height="100" fill="url(#crateGreen)" />
          <rect x="345" y="490" width="100" height="105" fill="url(#crateRed)" transform="rotate(-3 395 542)" />
          <rect x="455" y="540" width="80" height="90" fill="url(#crateLight)" transform="rotate(2 495 585)" />
          <rect x="360" y="390" width="85" height="95" fill="url(#crateBlue)" transform="rotate(4 402 437)" />
        </g>

        {/* Right stack - tall and precarious */}
        <g className="crate-stack-right">
          <rect x="1150" y="730" width="140" height="120" fill="url(#crateMedium)" />
          <rect x="1300" y="760" width="110" height="90" fill="url(#crateLight)" />
          <rect x="1160" y="610" width="120" height="115" fill="url(#crateLight)" />
          <rect x="1290" y="650" width="100" height="100" fill="url(#crateGreen)" />
          <rect x="1175" y="500" width="105" height="105" fill="url(#crateBlue)" transform="rotate(2 1227 552)" />
          <rect x="1290" y="545" width="85" height="95" fill="url(#crateMedium)" transform="rotate(-3 1332 592)" />
          <rect x="1190" y="395" width="90" height="100" fill="url(#crateRed)" transform="rotate(-4 1235 445)" />
          <rect x="1300" y="450" width="75" height="85" fill="url(#crateLight)" transform="rotate(5 1337 492)" />
          <rect x="1210" y="300" width="70" height="90" fill="url(#crateMedium)" transform="rotate(3 1245 345)" />
          <rect x="1225" y="215" width="55" height="75" fill="url(#crateGreen)" transform="rotate(-5 1252 252)" />
        </g>

        {/* Center floor crates */}
        <g className="crates-floor">
          <rect x="600" y="780" width="100" height="70" fill="url(#crateLight)" />
          <rect x="710" y="770" width="90" height="80" fill="url(#crateMedium)" />
          <rect x="810" y="785" width="80" height="65" fill="url(#crateRed)" />
          <rect x="620" y="710" width="85" height="65" fill="url(#crateGreen)" />
          <rect x="720" y="695" width="75" height="70" fill="url(#crateBlue)" />
        </g>

        {/* Concrete floor */}
        <rect x="0" y="850" width="1440" height="50" fill="#303030" />
        <line x1="0" y1="850" x2="1440" y2="850" stroke="#404040" strokeWidth="2" />

        {/* Floor markings */}
        <g className="floor-markings" opacity="0.3">
          <rect x="200" y="860" width="100" height="10" fill="#ffcc00" />
          <rect x="600" y="860" width="150" height="10" fill="#ffcc00" />
          <rect x="1100" y="860" width="120" height="10" fill="#ffcc00" />
        </g>

        {/* Crate details - wood grain lines */}
        <g className="crate-details" stroke="#00000030" strokeWidth="1" fill="none">
          {/* Horizontal lines on some crates */}
          <line x1="25" y1="780" x2="135" y2="780" />
          <line x1="25" y1="810" x2="135" y2="810" />
          <line x1="325" y1="760" x2="445" y2="760" />
          <line x1="325" y1="800" x2="445" y2="800" />
          <line x1="1155" y1="770" x2="1285" y2="770" />
          <line x1="1155" y1="810" x2="1285" y2="810" />
        </g>
      </svg>

      {/* Dust particles floating in light */}
      <Box className="warehouse-dust" position="absolute" width="100%" height="100%" top="0" left="0">
        {[...Array(15)].map((_, i) => (
          <Box
            key={i}
            className={`dust-mote dust-mote-${i + 1}`}
            position="absolute"
            width={2 + (i % 3) + "px"}
            height={2 + (i % 3) + "px"}
            borderRadius="50%"
            backgroundColor="#ffffe080"
            opacity="0"
          />
        ))}
      </Box>

      {/* Ambient shadows */}
      <Box
        position="absolute"
        bottom="0"
        left="0"
        width="100%"
        height="30%"
        background="linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.3) 100%)"
        pointerEvents="none"
      />
    </Box>
  );
};

export default WarehouseBackground;
